class PlayroomBag extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.queue = Promise.resolve();
    this.dialog = this.querySelector('dialog');
    this.labels = JSON.parse(this.querySelector('[data-bag-labels]').textContent);
    const signal = this.controller.signal;
    this.querySelectorAll('[data-bag-close]').forEach(button => button.addEventListener('click', () => this.dialog.close(), { signal }));
    this.dialog.addEventListener('close', () => this.returnFocus?.focus(), { signal });
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, { signal });
    window.addEventListener('pageshow', () => this.refresh().catch(() => this.unknown()), { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refresh().catch(() => this.unknown());
    }, { signal });
    this.refresh().catch(() => this.unknown());
  }
  disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  root() { const root = window.Shopify?.routes?.root || this.dataset.root; return root.endsWith('/') ? root : root + '/'; }
  serial(action) {
    const next = this.queue.then(action);
    this.queue = next.catch(() => {});
    return next;
  }
  unknown() { document.querySelectorAll('[data-bag-count]').forEach(node => { node.textContent = '(…)'; }); }
  apply(cart) {
    if (!Number.isInteger(cart.item_count)) throw new Error(this.labels.error);
    document.querySelectorAll('[data-bag-count]').forEach(node => { node.textContent = `(${cart.item_count})`; });
    document.dispatchEvent(new CustomEvent('playroom:cart-change', { detail: { cart } }));
    return cart;
  }
  async read() {
    const response = await fetch(this.root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(20000) });
    if (!response.ok) throw new Error(this.labels.error);
    return this.apply(await response.json());
  }
  refresh() { return this.serial(() => this.read()); }
  async request(endpoint, body) {
    const response = await fetch(this.root() + endpoint, {
      method: 'POST', headers: { Accept: 'application/json', ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }) },
      body: body instanceof FormData ? body : JSON.stringify(body), signal: AbortSignal.timeout(20000)
    });
    let data;
    try { data = await response.json(); } catch { throw new Error(this.labels.uncertain); }
    if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : this.labels.error);
    return data;
  }
  add(body, trigger) {
    return this.serial(async () => {
      let added;
      try { added = await this.request('cart/add.js', body); }
      catch (error) {
        await this.read().catch(() => this.unknown());
        throw new Error(error instanceof TypeError || error.name === 'TimeoutError' ? this.labels.uncertain : error.message);
      }
      let cart;
      try { cart = await this.read(); } catch { this.unknown(); }
      const item = added.items?.[0] || added;
      const title = [item.product_title || item.title, item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''].filter(Boolean).join(' — ');
      const quantity = cart?.items.find(line => line.key === item.key)?.quantity ?? item.quantity;
      this.querySelector('[data-bag-item]').textContent = title && Number.isInteger(quantity) ? this.labels.item.replace('[title]', title).replace('[quantity]', quantity) : '';
      this.querySelector('[data-bag-summary]').textContent = cart ? this.labels.summary.replace('[count]', cart.item_count).replace('[total]', new Intl.NumberFormat(document.documentElement.lang, { style: 'currency', currency: cart.currency }).format(cart.total_price / 100)) : this.labels.confirmed;
      this.returnFocus = trigger;
      document.querySelectorAll('[data-menu][open]').forEach(menu => { menu.open = false; });
      document.querySelectorAll('[data-search-close]').forEach(button => { if (button.getClientRects().length) button.click(); });
      if (!this.dialog.open) this.dialog.showModal();
      this.querySelector('[data-bag-view]').focus();
      return { added, cart };
    });
  }
  change(body) {
    return this.serial(async () => {
      try { return this.apply(await this.request('cart/change.js', body)); }
      catch (error) { await this.read().catch(() => this.unknown()); throw error; }
    });
  }
}
if (!customElements.get('playroom-bag')) customElements.define('playroom-bag', PlayroomBag);
